import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, CircularProgress,
  Alert, Divider, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, LinearProgress
} from '@mui/material'
import {
  PlayArrow, SkipNext, Visibility, Casino, StopCircle, Download,
  Fullscreen, People, EmojiEvents, ContentCopy
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useEventQuizSocket, playSound } from '../../hooks/useEventQuizSocket'
import { eventQuizAPI } from '../../services/eventQuizAPI'
import JoinQRCode from '../../components/EventQuiz/JoinQRCode'
import Leaderboard from '../../components/EventQuiz/Leaderboard'
import EventTimer from '../../components/EventQuiz/EventTimer'
import BonusWheel from '../../components/EventQuiz/BonusWheel'
import PodiumScreen from '../../components/EventQuiz/PodiumScreen'
import Signature from '../../components/Signature'
import toast from 'react-hot-toast'

export default function EventQuizHost() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const hostSecret = localStorage.getItem(`event-host-${sessionId}`)

  const [sessionData, setSessionData] = useState(null)
  const [quizSettings, setQuizSettings] = useState({ enableBonusWheel: true })
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [leaderboard, setLeaderboard] = useState([])
  const [timerRemaining, setTimerRemaining] = useState(0)
  const [phase, setPhase] = useState('waiting') // waiting|countdown|question|review|bonus-wheel|finished
  const [bonusWheelSpinning, setBonusWheelSpinning] = useState(false)
  const [bonusResult, setBonusResult] = useState(null)
  const [endDialogOpen, setEndDialogOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [participantCount, setParticipantCount] = useState(0)
  const [firstPlaceAlert, setFirstPlaceAlert] = useState(null)
  const firstPlaceTimeout = useRef(null)

  const { connected, state, on, emit } = useEventQuizSocket({
    roomCode: sessionData?.roomCode,
    role: 'host',
    hostSecret,
    enabled: !!sessionData?.roomCode && !!hostSecret
  })

  // Charger la session au montage
  useEffect(() => {
    if (!hostSecret) {
      toast.error('Session introuvable. Retournez à la liste.')
      navigate('/event-quiz')
      return
    }
    eventQuizAPI.getSession(sessionId)
      .then(res => {
        setSessionData(res.data.session)
        if (res.data.quiz?.settings) setQuizSettings(res.data.quiz.settings)
        setPhase(res.data.session.status)
        setLeaderboard(res.data.session.leaderboard || [])
        setParticipantCount(res.data.session.participantCount || 0)
        if (res.data.currentQuestion) setCurrentQuestion(res.data.currentQuestion)
      })
      .catch(() => {
        toast.error('Impossible de charger la session')
        navigate('/event-quiz')
      })
  }, [sessionId, hostSecret, navigate])

  // Sync état initial depuis socket
  useEffect(() => {
    if (state) {
      if (state.quiz?.settings) setQuizSettings(state.quiz.settings)
      setPhase(state.session?.status || phase)
      setLeaderboard(state.session?.leaderboard || [])
      setParticipantCount(state.session?.participantCount || 0)
      setTimerRemaining(state.session?.timerRemaining || 0)
      if (state.currentQuestion) setCurrentQuestion(state.currentQuestion)
    }
  }, [state])

  // Enregistrer les écouteurs socket
  useEffect(() => {
    on('event-participant-update', ({ participantCount: count, leaderboard: lb }) => {
      setParticipantCount(count)
      setLeaderboard(lb)
    })

    on('event-countdown', () => setPhase('countdown'))

    on('event-question-start', ({ question, timerRemaining: t }) => {
      setCurrentQuestion(question)
      setTimerRemaining(t)
      setPhase('question')
      setBonusResult(null)
      if (question.isJackpot) playSound('jackpot')
    })

    on('event-timer-tick', ({ timerRemaining: t }) => {
      setTimerRemaining(t)
      if (t <= 5 && t > 0) playSound('tick')
    })

    on('event-timer-end', ({ leaderboard: lb }) => {
      setLeaderboard(lb)
      setPhase('review')
    })

    on('event-review', ({ leaderboard: lb }) => {
      setLeaderboard(lb)
      setPhase('review')
    })

    on('event-leaderboard', ({ leaderboard: lb }) => setLeaderboard(lb))

    on('event-bonus-wheel', ({ result }) => {
      setPhase('bonus-wheel')
      setBonusWheelSpinning(true)
      setBonusResult(null)
      setTimeout(() => {
        setBonusResult(result)
        setBonusWheelSpinning(false)
      }, 4200)
    })

    on('event-bonus-applied', () => setPhase('review'))

    on('event-finished', ({ leaderboard: lb }) => {
      setLeaderboard(lb)
      setPhase('finished')
      playSound('first-place')
    })

    on('event-first-place', ({ nickname }) => {
      clearTimeout(firstPlaceTimeout.current)
      setFirstPlaceAlert(nickname)
      playSound('first-place')
      firstPlaceTimeout.current = setTimeout(() => setFirstPlaceAlert(null), 3000)
    })
  }, [on])

  const handleStartCountdown = useCallback(() => {
    emit('event-start-countdown', { hostSecret })
  }, [emit, hostSecret])

  const handleNextQuestion = useCallback(() => {
    emit('event-next-question', { hostSecret })
  }, [emit, hostSecret])

  const handleShowReview = useCallback(() => {
    emit('event-show-review', { hostSecret })
  }, [emit, hostSecret])

  const handleSpinBonus = useCallback(() => {
    emit('event-spin-bonus', { hostSecret })
  }, [emit, hostSecret])

  const handleEndSession = useCallback(async () => {
    emit('event-end-session', { hostSecret })
    setEndDialogOpen(false)
  }, [emit, hostSecret])

  const handleExport = useCallback(async (format) => {
    setExporting(true)
    try {
      const res = await eventQuizAPI.exportResults(sessionId, format)
      if (format === 'csv') {
        const url = URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `event-quiz-${sessionData?.roomCode}.csv`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        const url = URL.createObjectURL(new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' }))
        const a = document.createElement('a')
        a.href = url
        a.download = `event-quiz-${sessionData?.roomCode}.json`
        a.click()
        URL.revokeObjectURL(url)
      }
      toast.success('Export réussi')
    } catch {
      toast.error('Erreur lors de l\'export')
    } finally {
      setExporting(false)
    }
  }, [sessionId, sessionData?.roomCode])

  const handleCopyCode = useCallback(() => {
    navigator.clipboard.writeText(sessionData?.roomCode || '')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [sessionData?.roomCode])

  if (!sessionData) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
  }

  if (phase === 'finished') {
    return (
      <Box>
        <PodiumScreen winners={leaderboard.slice(0, 3)} title="Compétition terminée !" />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4, pb: 4 }}>
          <Button variant="contained" startIcon={<Download />} onClick={() => handleExport('csv')} disabled={exporting}>
            Exporter CSV
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={() => handleExport('json')} disabled={exporting}>
            Exporter JSON
          </Button>
          <Button variant="outlined" onClick={() => navigate('/event-quiz')}>
            Retour à la liste
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Alerte premier rang */}
      <AnimatePresence>
        {firstPlaceAlert && (
          <motion.div
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}
          >
            <Chip
              icon={<EmojiEvents />}
              label={`🥇 ${firstPlaceAlert} prend la tête !`}
              color="warning"
              sx={{ fontSize: '1rem', py: 3, px: 2, boxShadow: 4 }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">🎙 Panneau Organisateur</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={sessionData.roomCode}
              color="primary"
              size="small"
              sx={{ fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: 2, fontSize: '1rem' }}
            />
            <Tooltip title={copied ? 'Copié !' : 'Copier le code'}>
              <IconButton size="small" onClick={handleCopyCode}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Chip
              icon={<People />}
              label={`${participantCount} participant${participantCount > 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={connected ? '● Connecté' : '○ Déconnecté'}
              size="small"
              color={connected ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Vue écran géant (nouvel onglet)">
            <IconButton onClick={() => window.open(`/event-quiz/display/${sessionData.roomCode}`, '_blank')}>
              <Fullscreen />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="error"
            startIcon={<StopCircle />}
            onClick={() => setEndDialogOpen(true)}
            size="small"
          >
            Terminer
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Colonne gauche : contrôles */}
        <Grid item xs={12} md={7}>

          {/* Phase : salle d'attente */}
          {phase === 'waiting' && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <JoinQRCode code={sessionData.roomCode} size={200} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                  En attente de participants…
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  onClick={handleStartCountdown}
                  disabled={participantCount === 0}
                  sx={{ px: 5, py: 1.5, fontSize: '1.1rem' }}
                >
                  Lancer la compétition
                </Button>
                {participantCount === 0 && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Au moins 1 participant requis
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Phase : compte à rebours */}
          {phase === 'countdown' && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h4" fontWeight="bold">Démarrage…</Typography>
                <CircularProgress sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          )}

          {/* Phase : question en cours */}
          {phase === 'question' && currentQuestion && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    {currentQuestion.isJackpot && (
                      <Chip label="🎰 JACKPOT ×2" color="warning" sx={{ mb: 1 }} />
                    )}
                    <Typography variant="overline" color="text.secondary">Question en cours</Typography>
                    <Typography variant="h6" fontWeight="bold">{currentQuestion.question}</Typography>
                    <Chip label={currentQuestion.type} size="small" sx={{ mt: 1 }} />
                  </Box>
                  <Box sx={{ textAlign: 'center', ml: 2 }}>
                    <EventTimer seconds={timerRemaining} large />
                  </Box>
                </Box>

                {currentQuestion.media?.url && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    {currentQuestion.media.type === 'image' && (
                      <img src={currentQuestion.media.url} alt="" style={{ maxHeight: 180, borderRadius: 8 }} />
                    )}
                    {currentQuestion.media.type === 'audio' && <audio controls src={currentQuestion.media.url} />}
                    {currentQuestion.media.type === 'video' && (
                      <video controls src={currentQuestion.media.url} style={{ maxHeight: 160, borderRadius: 8 }} />
                    )}
                  </Box>
                )}

                <LinearProgress
                  variant="determinate"
                  value={currentQuestion.timeLimit ? (timerRemaining / currentQuestion.timeLimit) * 100 : 0}
                  color={timerRemaining <= 5 ? 'error' : timerRemaining <= 10 ? 'warning' : 'primary'}
                  sx={{ mb: 2, height: 6, borderRadius: 3 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Visibility />}
                  onClick={handleShowReview}
                >
                  Afficher la correction
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Phase : correction */}
          {phase === 'review' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Résultats de la question</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {quizSettings?.enableBonusWheel && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<Casino />}
                      onClick={handleSpinBonus}
                      sx={{ flex: 1 }}
                    >
                      Roue Bonus
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SkipNext />}
                    onClick={handleNextQuestion}
                    sx={{ flex: 1 }}
                  >
                    Question suivante
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Phase : roue bonus */}
          {phase === 'bonus-wheel' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <BonusWheel spinning={bonusWheelSpinning} result={bonusResult} />
                {bonusResult && !bonusWheelSpinning && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SkipNext />}
                    onClick={handleNextQuestion}
                    sx={{ mt: 2 }}
                  >
                    Question suivante
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Colonne droite : classement live */}
        <Grid item xs={12} md={5}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmojiEvents color="warning" />
                <Typography variant="h6" fontWeight="bold">Classement live</Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {leaderboard.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  Aucune réponse encore
                </Typography>
              ) : (
                <Leaderboard entries={leaderboard.slice(0, 10)} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog confirmation fin */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)}>
        <DialogTitle>Terminer la compétition ?</DialogTitle>
        <DialogContent>
          <Typography>
            Cette action arrête la session pour tous les participants. Les résultats seront disponibles à l'export.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialogOpen(false)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleEndSession}>
            Terminer maintenant
          </Button>
        </DialogActions>
      </Dialog>

      <Signature sx={{ mt: 4 }} />
    </Box>
  )
}
